import dayjs from 'dayjs'
import {Matcher} from 'react-day-picker'
import {ChangeEvent, forwardRef, useCallback, useEffect, useMemo, useState} from 'react'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {CalendarIcon} from '@/components/ui/icon/Icon'
import {Label} from '@/components/ui/label/Label'
import {useTranslation} from '@/translations/i18n'
import {StyledDatePickerWrapper, StyledInputDateCalendarButton, StyledInputDateText, StyledPopover} from './style'
import {PopoverDisclosure, PopoverProvider} from '@ariakit/react'
import {CalendarPicker} from '@/components/commons/calendar/calendar-picker/CalendarPicker'
import {formatDateInput, isValidDate} from '@/components/commons/input-date/utils'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {formatDate} from '@/dayjs'

interface InputDateProps {
    label?: string
    name?: string
    disabled?: boolean
    placeholder?: string
    fullWidth?: boolean
    isOptional?: boolean
    value?: Date | null
    dateFormat?: string
    defaultValue?: Date | null
    defaultMonth?: Date
    errorMessage?: string
    disabledDates?: Matcher | Matcher[]
    warningDates?: Matcher | Matcher[]
    disableBeforeDate?: Date
    disableAfterDate?: Date
    highlightAvailabilities?: boolean
    onBlur?: () => void
    onChange?: (date: Date | null) => void
    onMonthChange?: (date: Date) => void
    onDateOutOfPeriod?: (date: Date) => void
    required?: boolean
    portal?: boolean
}

export const InputDate = forwardRef<HTMLDivElement, InputDateProps>(
    (
        {
            label,
            isOptional,
            fullWidth,
            placeholder,
            name,
            disabled,
            onBlur,
            errorMessage,
            value,
            onChange,
            dateFormat = 'DD/MM/YYYY',
            onMonthChange,
            defaultValue,
            defaultMonth,
            disabledDates,
            warningDates,
            onDateOutOfPeriod,
            highlightAvailabilities,
            disableBeforeDate,
            disableAfterDate,
            required,
            portal
        }: InputDateProps,
        ref
    ) => {
        const {t} = useTranslation()

        // Internal state for tracking the current date value and displayed month
        const [internalDate, setInternalDate] = useState<Date | null>(defaultValue || null)
        const [inputValue, setInputValue] = useState<string>(defaultValue ? dayjs(defaultValue).format(dateFormat) : '')
        const [month, setMonth] = useState<Date>(defaultMonth || defaultValue || value || new Date())
        const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false)

        // Handle isControlled mode
        const isControlled = value !== undefined
        const currentDate = isControlled ? value : internalDate

        // Prepare disabled dates array for CalendarPicker
        const calendarDisabledDates = useMemo(() => {
            const disabledArray: Matcher[] = []

            // Add specific disabled dates
            if (disabledDates) {
                if (Array.isArray(disabledDates)) {
                    disabledArray.push(...disabledDates)
                } else {
                    disabledArray.push(disabledDates)
                }
            }

            // Add date range restrictions
            if (disableBeforeDate) {
                disabledArray.push({before: disableBeforeDate})
            }

            if (disableAfterDate) {
                disabledArray.push({after: disableAfterDate})
            }

            return disabledArray.length > 0 ? disabledArray : undefined
        }, [disabledDates, disableBeforeDate, disableAfterDate])

        const calendarWarningDates = useMemo(() => {
            const disabledArray: Matcher[] = []

            // Add specific disabled dates
            if (warningDates) {
                if (Array.isArray(warningDates)) {
                    disabledArray.push(...warningDates)
                } else {
                    disabledArray.push(warningDates)
                }
            }

            // Add date range restrictions
            if (disableBeforeDate) {
                disabledArray.push({before: disableBeforeDate})
            }

            if (disableAfterDate) {
                disabledArray.push({after: disableAfterDate})
            }

            return disabledArray.length > 0 ? disabledArray : undefined
        }, [warningDates, disableBeforeDate, disableAfterDate])

        // Enhance the disabled dates handling
        const isDateDisabled = useCallback(
            (date: Date | null): boolean => {
                if (!date) return false

                // Check against specific disabled dates
                if (disabledDates) {
                    if (Array.isArray(disabledDates)) {
                        // Check each matcher in the array
                        for (const matcher of disabledDates) {
                            // Handle Date objects
                            if (matcher instanceof Date) {
                                if (formatDate(matcher) === formatDate(date)) {
                                    return true
                                }
                            }
                            // Handle before/after objects
                            else if (typeof matcher === 'object') {
                                if ('before' in matcher && matcher.before instanceof Date) {
                                    if (dayjs(date).isBefore(dayjs(matcher.before), 'day')) {
                                        return true
                                    }
                                }
                                if ('after' in matcher && matcher.after instanceof Date) {
                                    if (dayjs(date).isAfter(dayjs(matcher.after), 'day')) {
                                        return true
                                    }
                                }
                                if ('from' in matcher && 'to' in matcher) {
                                    if (
                                        matcher.from instanceof Date &&
                                        matcher.to instanceof Date &&
                                        dayjs(date).isAfter(dayjs(matcher.from), 'day') &&
                                        dayjs(date).isBefore(dayjs(matcher.to), 'day')
                                    ) {
                                        return true
                                    }
                                }
                            }
                        }
                    } else {
                        // Handle single matcher
                        if (disabledDates instanceof Date) {
                            return formatDate(disabledDates) === formatDate(date)
                        }
                    }
                }

                // Check against date ranges
                if (disableBeforeDate && dayjs(date).isBefore(dayjs(disableBeforeDate), 'day')) {
                    return true
                }

                if (disableAfterDate && dayjs(date).isAfter(dayjs(disableAfterDate), 'day')) {
                    return true
                }

                return false
            },
            [disabledDates, disableBeforeDate, disableAfterDate]
        )

        // Helper function to handle invalid/disabled date selection
        const handleInvalidDate = useCallback(
            (date: Date | null, reason: 'disabled' | 'invalid' | 'out-of-range') => {
                // Only call onDateOutOfPeriod if we have a date to report
                if (date && (reason == 'disabled' || reason == 'out-of-range')) {
                    setInputValue('')
                    setMonth(new Date())
                    onDateOutOfPeriod?.(date)
                }

                // Clear internal state if uncontrolled
                if (!isControlled) {
                    setInternalDate(null)
                }

                // Notify parent component
                if (onChange && currentDate) {
                    onChange(null)
                }
            },
            [isControlled, onChange, currentDate, onDateOutOfPeriod]
        )

        const handleInputChange = useCallback(
            (e: ChangeEvent<HTMLInputElement>) => {
                let formattedValue = e.target.value

                // Allow number or slashes
                formattedValue = formatDateInput(formattedValue, dateFormat)

                // Preserve leading zeros
                if (formattedValue.length <= 10) {
                    setInputValue(formattedValue)

                    // Check if we have a complete and valid date
                    const isCompleteFormat = formattedValue.length === dateFormat.length

                    if (isCompleteFormat && isValidDate(formattedValue, dateFormat)) {
                        // We have a valid date, convert it
                        const newDate = dayjs(formattedValue, dateFormat).toDate()

                        if (isDateDisabled(newDate)) {
                            // Keep the input value but don't update the actual date
                            handleInvalidDate(newDate, 'disabled')
                            return
                        }

                        if (!isControlled) {
                            setInternalDate(newDate)
                        }

                        // Update calendar month
                        setMonth(newDate)

                        // Notify parent component
                        onChange?.(newDate)
                    } else if (formattedValue == '') {
                        // Input was cleared, reset the date
                        handleInvalidDate(null, 'invalid')
                    } else if (isCompleteFormat) {
                        // Input has correct format but date is invalid (like 31/02/2023)
                        // Clear the internal date but keep the input text to allow editing
                        handleInvalidDate(null, 'invalid')
                    } else {
                        // Partial date - try to extract month and year information if possible
                        // to update the calendar view as the user is typing
                        try {
                            // Check if we have at least day and month (DD/MM)
                            if (formattedValue.match(/^\d{2}\/\d{2}$/)) {
                                const [, month] = formattedValue.split('/').map(Number)

                                // Only update if the month is valid
                                if (month >= 1 && month <= 12) {
                                    // Create a date with the current year and typed month
                                    const currentYear = new Date().getFullYear()
                                    const previewDate = new Date(currentYear, month - 1, 15) // Use day 15 to avoid month rollover

                                    // Update calendar month without changing the selected date
                                    setMonth(previewDate)
                                }
                            }
                        } catch (e) {
                            // If any error occurs during parsing, just ignore it
                            // This is just a preview enhancement
                        }

                        // Clear the selected date through the common handler
                        handleInvalidDate(null, 'invalid')
                    }
                }
            },
            [inputValue, onChange, dateFormat, isDateDisabled, handleInvalidDate]
        )

        // Update handleCalendarSelect to check for disabled dates
        const handleCalendarSelect = useCallback(
            (date: Date, closeCalendarAfterSelect: boolean = true) => {
                if (isDateDisabled(date)) {
                    handleInvalidDate(date, 'disabled')
                    return
                }

                // Date is valid, proceed with selection
                if (!isControlled) {
                    setInternalDate(date)
                }

                setMonth(date)
                setInputValue(dayjs(date).format(dateFormat))
                setIsCalendarOpen(!closeCalendarAfterSelect)
                onChange?.(date)
            },
            [onChange, isDateDisabled, handleInvalidDate, isControlled, dateFormat]
        )

        // Update value when defaultValue changes (rare, but possible)
        useEffect(() => {
            if (!isControlled && defaultValue != undefined) {
                setInternalDate(defaultValue)
                setInputValue(defaultValue ? dayjs(defaultValue).format(dateFormat) : '')
                setMonth(defaultValue || new Date())
            }
        }, [isControlled, defaultValue, dateFormat])

        // Update input value when controlled value changes
        useEffect(() => {
            if (isControlled) {
                const isValueValid = value && dayjs(value).isValid()

                if (isValueValid && dayjs(value).format(dateFormat).length == dateFormat.length) {
                    setInputValue(dayjs(value).format(dateFormat))
                } else if (!value || !isValueValid) {
                    setInputValue('')
                }

                // Update month only if value changes significantly (different month/year)
                if (
                    isValueValid &&
                    (!month || value.getMonth() !== month.getMonth() || value.getFullYear() !== month.getFullYear())
                ) {
                    setMonth(value)
                }
            }
        }, [isControlled, value, dateFormat])

        // Update input value to clear the date when the controlled value is null
        useEffect(() => {
            if (isControlled && value === null) {
                setInputValue('')
            }
        }, [isControlled, value])

        return (
            <div ref={ref} style={{width: fullWidth ? '100%' : 'auto'}}>
                <Flexbox direction="column" gap={1.5}>
                    {label && (
                        <Label htmlFor={name}>
                            {label} {isOptional && <span className="description">{t('commons:optional')}</span>}
                            {required && !disabled && <span className="description">{t('errors:required')}</span>}
                        </Label>
                    )}

                    <Flexbox fullWidth={fullWidth}>
                        <PopoverProvider
                            placement="bottom-start"
                            setOpen={open => {
                                setIsCalendarOpen(open)
                                if (open) {
                                    onMonthChange?.(month)
                                } else {
                                    onBlur?.()
                                }
                            }}
                            open={disabled ? false : isCalendarOpen}
                        >
                            <PopoverDisclosure
                                render={
                                    <StyledInputDateCalendarButton
                                        shape="square"
                                        variant="secondary"
                                        $hasError={!!errorMessage}
                                        disabled={disabled}
                                    >
                                        <CalendarIcon />
                                    </StyledInputDateCalendarButton>
                                }
                            />
                            <StyledPopover gutter={4} portal={portal}>
                                <StyledDatePickerWrapper>
                                    <CalendarPicker
                                        month={month}
                                        onSelect={handleCalendarSelect}
                                        onNextMonth={() => setMonth(dayjs(month).add(1, 'month').toDate())}
                                        onPreviousMonth={() => setMonth(dayjs(month).subtract(1, 'month').toDate())}
                                        onMonthChange={onMonthChange}
                                        highlightAvailabilities={highlightAvailabilities}
                                        selected={currentDate ?? undefined}
                                        disabledDates={calendarDisabledDates}
                                        warningDates={calendarWarningDates}
                                    />
                                </StyledDatePickerWrapper>
                            </StyledPopover>
                        </PopoverProvider>

                        <StyledInputDateText
                            name={name}
                            disabled={disabled}
                            placeholder={placeholder}
                            type="text"
                            onChange={handleInputChange}
                            onBlur={() => setIsCalendarOpen(false)}
                            $hasError={!!errorMessage}
                            value={inputValue}
                        />
                    </Flexbox>
                    {errorMessage && <InputHelpText error={errorMessage} />}
                </Flexbox>
            </div>
        )
    }
)
